import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, RiskBreakdownResponse } from '../../services/analyticsService';

interface TopRisksChartProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

/**
 * Interpolate between green and red based on a 0-1 ratio.
 * Higher ratio = more red (higher severity), lower = more green.
 */
const getSeverityColor = (ratio: number): string => {
  // Green (low) -> Yellow (mid) -> Red (high)
  const r = Math.round(ratio > 0.5 ? 255 : 255 * (ratio / 0.5));
  const g = Math.round(ratio < 0.5 ? 200 : 200 * (1 - (ratio - 0.5) / 0.5));
  const b = 60;
  return `rgb(${r}, ${g}, ${b})`;
};

const TopRisksChart: React.FC<TopRisksChartProps> = ({
  businessIdentifier,
}) => {
  const [data, setData] = useState<RiskBreakdownResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getRiskBreakdown(businessIdentifier);
        setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch risk breakdown data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier]);

  const getChartOption = () => {
    if (!data || !data.risks || data.risks.length === 0) return {};

    // Sort by count ascending so the highest is at the top of the horizontal bar chart
    const sorted = [...data.risks].sort((a, b) => a.count - b.count);
    const categories = sorted.map((r) => r.category);
    const counts = sorted.map((r) => r.count);
    const percentages = sorted.map((r) => r.percentage);

    const maxCount = Math.max(...counts, 1);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = sorted[idx];
          if (!item) return '';
          return `<b>${item.category}</b><br/>Count: ${item.count}<br/>Percentage: ${item.percentage.toFixed(1)}%`;
        },
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 130,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          data: counts.map((count, idx) => ({
            value: count,
            itemStyle: {
              color: getSeverityColor(count / maxCount),
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            formatter: (params: any) => {
              const idx = params.dataIndex;
              return `${params.value} (${percentages[idx]?.toFixed(1)}%)`;
            },
          },
        },
      ],
    };
  };

  const hasData = data && data.risks && data.risks.length > 0;

  return (
    <ChartCard title="Top Risks Breakdown" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, data!.risks.length * 40) + 'px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            {!businessIdentifier ? 'Select a business to view data' : 'No data available'}
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default TopRisksChart;
